import React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface EmailLogDetail {
  id: number;
  recipient: string;
  subject: string;
  mailerClass: string;
  mailerAction: string;
  messageId: string | null;
  status: 'sent' | 'delivered' | 'bounced' | 'failed' | 'complained';
  sentAt: string | null;
  body: string | null;
  bounceType: string | null;
  bouncedAt: string | null;
  complainedAt: string | null;
  complaintType: string | null;
  deliveryError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminEmailLogDetailPageProps {
  emailLog: EmailLogDetail;

  // URLs
  emailLogsPath: string;

  // Labels
  labels: {
    title: string;
    backLabel: string;
    // Details
    emailDetailsTitle: string;
    recipientLabel: string;
    subjectLabel: string;
    mailerLabel: string;
    messageIdLabel: string;
    sentAtLabel: string;
    statusLabel: string;
    // Status badges
    deliveredStatus: string;
    bouncedStatus: string;
    complainedStatus: string;
    failedStatus: string;
    sentStatus: string;
    // Email body
    emailBodyTitle: string;
    // Status cards
    bounceInfoTitle: string;
    bounceTypeLabel: string;
    bouncedAtLabel: string;
    errorMessageLabel: string;
    unknownLabel: string;
    notApplicable: string;
    complaintInfoTitle: string;
    complaintTypeLabel: string;
    complainedAtLabel: string;
    complaintWarning: string;
    deliveryFailedTitle: string;
    unknownFailureReason: string;
    successfullyDeliveredTitle: string;
    successMessage: string;
    // Metadata
    metadataTitle: string;
    createdLabel: string;
    updatedLabel: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

function titleize(str: string | null): string {
  if (!str) return 'Unknown';
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ============================================================================
// Helper Components
// ============================================================================

function StatusBadge({
  status,
  labels,
}: {
  status: EmailLogDetail['status'];
  labels: AdminEmailLogDetailPageProps['labels'];
}) {
  const configs = {
    delivered: { bg: 'bg-success', label: labels.deliveredStatus },
    bounced: { bg: 'bg-warning', label: labels.bouncedStatus },
    complained: { bg: 'bg-danger', label: labels.complainedStatus },
    failed: { bg: 'bg-danger', label: labels.failedStatus },
    sent: { bg: 'bg-secondary', label: labels.sentStatus },
  };

  const config = configs[status] || configs.sent;

  return <span className={`badge ${config.bg}`}>{config.label}</span>;
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminEmailLogDetailPage({
  emailLog,
  emailLogsPath,
  labels,
}: AdminEmailLogDetailPageProps) {
  const isBounced = emailLog.status === 'bounced';
  const isComplained = emailLog.status === 'complained';
  const isFailed = emailLog.status === 'failed';
  const isDelivered = emailLog.status === 'delivered';

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <h1 className="h2">
            {labels.title} #{emailLog.id}
          </h1>
          <a href={emailLogsPath} className="text-muted">
            ← {labels.backLabel}
          </a>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8">
          {/* Email Details Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">{labels.emailDetailsTitle}</h5>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-sm-3 fw-bold">{labels.recipientLabel}:</div>
                <div className="col-sm-9">{emailLog.recipient}</div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-3 fw-bold">{labels.subjectLabel}:</div>
                <div className="col-sm-9">{emailLog.subject}</div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-3 fw-bold">{labels.mailerLabel}:</div>
                <div className="col-sm-9">
                  <code>
                    {emailLog.mailerClass}#{emailLog.mailerAction}
                  </code>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-3 fw-bold">{labels.messageIdLabel}:</div>
                <div className="col-sm-9">
                  <code>{emailLog.messageId || labels.notApplicable}</code>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-3 fw-bold">{labels.sentAtLabel}:</div>
                <div className="col-sm-9">{formatDateTime(emailLog.sentAt)}</div>
              </div>
              <div className="row">
                <div className="col-sm-3 fw-bold">{labels.statusLabel}:</div>
                <div className="col-sm-9">
                  <StatusBadge status={emailLog.status} labels={labels} />
                </div>
              </div>
            </div>
          </div>

          {/* Email Body Card */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">{labels.emailBodyTitle}</h5>
            </div>
            <div className="card-body">
              <div
                className="border p-3 bg-light"
                style={{ maxHeight: 600, overflowY: 'auto', whiteSpace: 'pre-wrap' }}
              >
                {emailLog.body || '-'}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          {/* Bounce Information */}
          {isBounced && (
            <div className="card border-warning mb-4">
              <div className="card-header bg-warning text-white">
                <h5 className="mb-0">
                  <i className="bi bi-exclamation-triangle me-2" />
                  {labels.bounceInfoTitle}
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-2">
                  <strong>{labels.bounceTypeLabel}:</strong>
                  <span className="badge bg-warning ms-2">
                    {titleize(emailLog.bounceType) || labels.unknownLabel}
                  </span>
                </div>
                <div className="mb-2">
                  <strong>{labels.bouncedAtLabel}:</strong>{' '}
                  {formatDateTime(emailLog.bouncedAt) || labels.notApplicable}
                </div>
                {emailLog.deliveryError && (
                  <div className="mt-3">
                    <strong>{labels.errorMessageLabel}:</strong>
                    <div className="alert alert-warning mt-2 mb-0">
                      <small>{emailLog.deliveryError}</small>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Complaint Information */}
          {isComplained && (
            <div className="card border-danger mb-4">
              <div className="card-header bg-danger text-white">
                <h5 className="mb-0">
                  <i className="bi bi-x-circle me-2" />
                  {labels.complaintInfoTitle}
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-2">
                  <strong>{labels.complaintTypeLabel}:</strong>
                  <span className="badge bg-danger ms-2">
                    {titleize(emailLog.complaintType) || labels.unknownLabel}
                  </span>
                </div>
                <div className="mb-2">
                  <strong>{labels.complainedAtLabel}:</strong>{' '}
                  {formatDateTime(emailLog.complainedAt) || labels.notApplicable}
                </div>
                <div className="alert alert-danger mt-3 mb-0">
                  <small>
                    <i className="bi bi-info-circle me-1" />
                    {labels.complaintWarning}
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Failed Information */}
          {isFailed && (
            <div className="card border-danger mb-4">
              <div className="card-header bg-danger text-white">
                <h5 className="mb-0">
                  <i className="bi bi-x-circle me-2" />
                  {labels.deliveryFailedTitle}
                </h5>
              </div>
              <div className="card-body">
                {emailLog.deliveryError ? (
                  <div className="alert alert-danger mb-0">
                    <small>{emailLog.deliveryError}</small>
                  </div>
                ) : (
                  <p className="text-muted mb-0">{labels.unknownFailureReason}</p>
                )}
              </div>
            </div>
          )}

          {/* Successfully Delivered */}
          {isDelivered && (
            <div className="card border-success mb-4">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0">
                  <i className="bi bi-check-circle me-2" />
                  {labels.successfullyDeliveredTitle}
                </h5>
              </div>
              <div className="card-body">
                <p className="text-muted mb-0">{labels.successMessage}</p>
              </div>
            </div>
          )}

          {/* Metadata Card */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">{labels.metadataTitle}</h5>
            </div>
            <div className="card-body">
              <div className="mb-2">
                <small className="text-muted">{labels.createdLabel}:</small>
                <br />
                {formatDateTime(emailLog.createdAt)}
              </div>
              <div>
                <small className="text-muted">{labels.updatedLabel}:</small>
                <br />
                {formatDateTime(emailLog.updatedAt)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminEmailLogDetailPage;
